import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Typography,
  Box,
  Divider,
  Chip,
} from '@mui/material';
import { ProgramSubscription, updateSubscription } from '../../services/pipelineService';

interface SubscriptionDetailDialogProps {
  open: boolean;
  subscription: ProgramSubscription;
  onClose: (refresh: boolean) => void;
}

interface EditFormData {
  subscriptionStatus: 'active' | 'paused' | 'cancelled' | 'completed';
  customSetupFee: string;
  customMonthlyFee: string;
  customLeadsPerWeek: string;
  notes: string;
}

const SubscriptionDetailDialog: React.FC<SubscriptionDetailDialogProps> = ({
  open,
  subscription,
  onClose,
}) => {
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState<EditFormData>({
    subscriptionStatus: subscription.subscription_status,
    customSetupFee: subscription.custom_setup_fee?.toString() || '',
    customMonthlyFee: subscription.custom_monthly_fee.toString(),
    customLeadsPerWeek: subscription.custom_leads_per_week?.toString() || '',
    notes: subscription.notes || '',
  });
  const [errors, setErrors] = useState<Partial<EditFormData>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (field: keyof EditFormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<EditFormData> = {};

    if (formData.customSetupFee) {
      const setupFee = parseFloat(formData.customSetupFee);
      if (isNaN(setupFee) || setupFee < 0) {
        newErrors.customSetupFee = 'Setup fee must be a positive number';
      }
    }

    if (formData.customMonthlyFee) {
      const monthlyFee = parseFloat(formData.customMonthlyFee);
      if (isNaN(monthlyFee) || monthlyFee < 0) {
        newErrors.customMonthlyFee = 'Monthly fee must be a positive number';
      }
    }

    if (formData.customLeadsPerWeek) {
      const leadsPerWeek = parseInt(formData.customLeadsPerWeek, 10);
      if (isNaN(leadsPerWeek) || leadsPerWeek < 0) {
        newErrors.customLeadsPerWeek = 'Leads per week must be a positive integer';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const updateData = {
        subscriptionStatus: formData.subscriptionStatus,
        customSetupFee: formData.customSetupFee ? parseFloat(formData.customSetupFee) : undefined,
        customMonthlyFee: formData.customMonthlyFee ? parseFloat(formData.customMonthlyFee) : undefined,
        customLeadsPerWeek: formData.customLeadsPerWeek ? parseInt(formData.customLeadsPerWeek, 10) : undefined,
        notes: formData.notes.trim() || undefined,
      };

      await updateSubscription(subscription.id, updateData);
      setEditing(false);
      onClose(true);
    } catch (err: any) {
      console.error('Error updating subscription:', err);
      setError(
        err.response?.data?.message ||
        'Failed to update subscription. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (editing) {
      // Reset form data
      setFormData({
        subscriptionStatus: subscription.subscription_status,
        customSetupFee: subscription.custom_setup_fee?.toString() || '',
        customMonthlyFee: subscription.custom_monthly_fee.toString(),
        customLeadsPerWeek: subscription.custom_leads_per_week?.toString() || '',
        notes: subscription.notes || '',
      });
      setEditing(false);
      setErrors({});
      setError(null);
    } else {
      onClose(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active':
        return 'success';
      case 'paused':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>Subscription Details</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Client Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Client Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Name
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {subscription.client_name || 'N/A'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {subscription.client_email || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Program Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Program Information
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Program
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {subscription.program_display_name || subscription.program_name || 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Subscription Details */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Subscription Details
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              {editing ? (
                <TextField
                  fullWidth
                  select
                  size="small"
                  value={formData.subscriptionStatus}
                  onChange={handleChange('subscriptionStatus')}
                  disabled={submitting}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="paused">Paused</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                </TextField>
              ) : (
                <Chip
                  label={subscription.subscription_status.charAt(0).toUpperCase() + subscription.subscription_status.slice(1)}
                  color={getStatusColor(subscription.subscription_status)}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Start Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {new Date(subscription.start_date).toLocaleDateString()}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Next Billing Date
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {subscription.next_billing_date
                  ? new Date(subscription.next_billing_date).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Pricing */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Pricing & Delivery
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Setup Fee
              </Typography>
              {editing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formData.customSetupFee}
                  onChange={handleChange('customSetupFee')}
                  error={!!errors.customSetupFee}
                  helperText={errors.customSetupFee}
                  disabled={submitting}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  ${parseFloat(subscription.custom_setup_fee || '0').toFixed(2)}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Monthly Fee
              </Typography>
              {editing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formData.customMonthlyFee}
                  onChange={handleChange('customMonthlyFee')}
                  error={!!errors.customMonthlyFee}
                  helperText={errors.customMonthlyFee}
                  disabled={submitting}
                  inputProps={{ min: 0, step: 0.01 }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  ${parseFloat(subscription.custom_monthly_fee).toFixed(2)}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="body2" color="text.secondary">
                Leads Per Week
              </Typography>
              {editing ? (
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={formData.customLeadsPerWeek}
                  onChange={handleChange('customLeadsPerWeek')}
                  error={!!errors.customLeadsPerWeek}
                  helperText={errors.customLeadsPerWeek}
                  disabled={submitting}
                  inputProps={{ min: 0, step: 1 }}
                />
              ) : (
                <Typography variant="body1" fontWeight="medium">
                  {subscription.custom_leads_per_week || 'N/A'}
                </Typography>
              )}
            </Grid>
          </Grid>
        </Box>

        {/* Notes */}
        <Box>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Notes
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {editing ? (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleChange('notes')}
              disabled={submitting}
              placeholder="Add notes about this subscription..."
            />
          ) : (
            <Typography variant="body2">
              {subscription.notes || 'No notes'}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} disabled={submitting}>
          {editing ? 'Cancel' : 'Close'}
        </Button>
        {editing ? (
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            Save Changes
          </Button>
        ) : (
          <Button onClick={() => setEditing(true)} variant="contained">
            Edit
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionDetailDialog;
